import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link,
  useTheme,
} from '@mui/material';
import { VpnKey as KeyIcon } from '@mui/icons-material';
import authService from '../../services/authService';

const VendorLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  // Check if already authenticated on component mount
  useEffect(() => {
    console.log("VendorLogin component mounted");
    
    // If already authenticated, redirect to dashboard
    if (authService.isAuthenticated()) {
      console.log("Already authenticated, redirecting to dashboard");
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    console.log("Login attempt with:", email);

    try {
      // Use auth service to login
      const success = await authService.login(email, password);
      
      if (success) {
        console.log("Login successful");
        
        // If login was successful, redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      } else {
        // Check if password change is required
        if (authService.isPasswordChangeRequired()) {
          console.log("Password change required - redirecting to change password page");
          navigate('/force-change-password', { replace: true });
          return;
        }
        
        console.log("Login failed - invalid credentials");
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ mb: 4, color: theme.palette.primary.main, fontWeight: 500 }}
        >
          ReasonShield Vendor Portal
        </Typography>

        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <KeyIcon sx={{ mr: 1.5, color: theme.palette.secondary.main }} />
              <Typography variant="h5" component="h2">
                Sign In
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </form>

            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Having trouble signing in?{' '}
                <Link href="mailto:support@reasonshield.com" underline="hover">
                  Contact Support
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default VendorLogin; 